import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { CheckCircle2, Gamepad2, LogIn, Snowflake, Users } from 'lucide-react'
import useAudioManager from '../../../../hooks/useAudioManager'
import { hapticImpact } from '../../../../utils/haptic'
import { formatPrice } from '../../../../utils/formatters'
import useCountdown from '../../hooks/useCountdown'
import { formatEventTimer } from '../../utils/halloween'
import { ProgressBar, PumpkinIcon } from './shared'

// Тыквенная кнопка главного действия блока
const PumpkinButton = ({ icon: Icon, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="hev-cta mt-4 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-black uppercase transition-transform active:scale-[0.98]"
  >
    <Icon size={18} />
    {children}
  </button>
)

// Краткие правила для тех, кто ещё не участвует
const QuickRules = ({ rules }) => {
  const { t } = useTranslation()
  if (!rules) return null
  const items = [
    t('halloweenEvent.player.rulePrice', { price: formatPrice(rules.pumpkin_price) }),
    t('halloweenEvent.player.ruleCap', { count: rules.daily_cap }),
    t('halloweenEvent.player.ruleTicket')
  ]
  return (
    <ul className="mt-4 space-y-2">
      {items.map((text) => (
        <li key={text} className="flex items-start gap-2 text-sm text-white/85">
          <PumpkinIcon size={16} className="mt-0.5" />
          <span>{text}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Блок игрока: экран выбирается строго по you.state (no_team / not_started / playing).
 * Гость видит приглашение войти. После окончания сбора (you.today = null)
 * вместо дневного прогресса — итог игрока и билеты банды.
 */
const PlayerCard = ({ phase, you, myTeam, rules, isGuest, onLogin }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { playSound } = useAudioManager({ select: 'select' })
  const today = you?.today
  const resetRemaining = useCountdown(today ? today.reset_in : null)

  const go = (path) => {
    playSound('select')
    hapticImpact('light')
    navigate(path)
  }

  const wrapperClass = 'hev-card relative overflow-hidden rounded-2xl p-5'

  if (isGuest || !you) {
    return (
      <section className={wrapperClass}>
        <p className="text-lg font-black leading-tight text-white">{t('halloweenEvent.player.guestTitle')}</p>
        <p className="mt-1 text-sm text-text-secondary">{t('halloweenEvent.player.guestText')}</p>
        <QuickRules rules={rules} />
        <PumpkinButton icon={LogIn} onClick={onLogin}>{t('guest.login')}</PumpkinButton>
      </section>
    )
  }

  // Сбор окончен: тыквы заморожены, показываем итог игрока и билеты банды
  if (phase !== 'active' || !today) {
    return (
      <section className={wrapperClass}>
        <p className="flex items-center gap-2 text-sm font-bold text-violet-200">
          <Snowflake size={16} className="text-violet-300" />
          {t('halloweenEvent.player.collectionOver')}
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-white/10 bg-black/30 p-3">
            <p className="text-[11px] text-text-secondary">{t('halloweenEvent.player.total')}</p>
            <p className="mt-1 flex items-center gap-1.5 text-2xl font-black tabular-nums text-white">
              <PumpkinIcon size={20} />
              {you.total_pumpkins}
            </p>
          </div>
          {myTeam && (
            <div className="rounded-xl border border-orange-400/25 bg-orange-500/10 p-3">
              <p className="text-[11px] text-text-secondary">{t('halloweenEvent.player.teamTickets')}</p>
              <p className="hev-flame-text mt-1 text-2xl font-black tabular-nums">{myTeam.pumpkins}</p>
            </div>
          )}
        </div>
      </section>
    )
  }

  if (you.state === 'no_team') {
    return (
      <section className={wrapperClass}>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-violet-400/30 bg-violet-500/15">
            <Users size={20} className="text-violet-200" />
          </div>
          <div className="min-w-0">
            <p className="text-lg font-black leading-tight text-white">{t('halloweenEvent.player.notInTitle')}</p>
            <p className="mt-0.5 text-sm text-text-secondary">
              {you.total_pumpkins > 0
                ? t('halloweenEvent.player.leftTeam', { count: you.total_pumpkins })
                : t('halloweenEvent.player.noTeam')}
            </p>
          </div>
        </div>
        <QuickRules rules={rules} />
        <PumpkinButton icon={Users} onClick={() => go('/teams')}>{t('halloweenEvent.player.findTeam')}</PumpkinButton>
      </section>
    )
  }

  if (you.state === 'not_started') {
    return (
      <section className={wrapperClass}>
        <p className="text-lg font-black leading-tight text-white">{t('halloweenEvent.player.notInTitle')}</p>
        <p className="mt-1 text-sm text-text-secondary">
          {t('halloweenEvent.player.notStarted', { price: formatPrice(rules?.pumpkin_price ?? 0) })}
        </p>
        <div className="mt-4 flex items-center gap-3">
          <PumpkinIcon size={36} dim />
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex items-center justify-between text-xs">
              <span className="text-text-secondary">{t('halloweenEvent.player.toFirst')}</span>
              <span className="font-bold tabular-nums text-orange-200">{today.next_pct ?? 0}%</span>
            </div>
            <ProgressBar pct={today.next_pct} />
          </div>
        </div>
        <PumpkinButton icon={Gamepad2} onClick={() => go('/games')}>{t('halloweenEvent.player.play')}</PumpkinButton>
      </section>
    )
  }

  // playing: дневная статистика «3 из 5» тыквами
  const cap = Math.max(0, Number(today.cap) || 0)
  return (
    <section className={wrapperClass}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-orange-300">{t('halloweenEvent.player.today')}</p>
        <p className="text-sm font-bold tabular-nums text-white">
          {t('halloweenEvent.player.todayCount', { count: today.pumpkins, cap })}
        </p>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {Array.from({ length: cap }, (_, index) => {
          const collected = index < today.pumpkins
          return (
            <motion.div
              key={index}
              initial={collected ? { scale: 0.4, rotate: -20, opacity: 0 } : false}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 320, damping: 16, delay: index * 0.07 }}
              className={`flex h-12 w-12 items-center justify-center rounded-xl border ${collected ? 'border-orange-400/40 bg-orange-500/15 shadow-[0_0_16px_rgba(255,120,20,0.25)]' : 'border-white/10 bg-black/30'}`}
            >
              <PumpkinIcon size={32} dim={!collected} glow={collected} />
            </motion.div>
          )
        })}
      </div>

      {today.done ? (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-accent-primary/25 bg-accent-primary/10 px-3 py-2.5">
          <CheckCircle2 size={16} className="shrink-0 text-accent-primary" />
          <p className="text-sm font-semibold text-white">{t('halloweenEvent.player.capDone')}</p>
        </div>
      ) : (
        <div className="mt-4">
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="text-text-secondary">{t('halloweenEvent.player.toNext')}</span>
            <span className="font-bold tabular-nums text-orange-200">{today.next_pct ?? 0}%</span>
          </div>
          <ProgressBar pct={today.next_pct} />
        </div>
      )}

      <p className="mt-2 text-xs tabular-nums text-text-secondary">
        {t('halloweenEvent.player.resetIn', { time: formatEventTimer(resetRemaining, t) })}
      </p>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-white/10 bg-black/30 p-3">
          <p className="text-[11px] text-text-secondary">{t('halloweenEvent.player.total')}</p>
          <p className="mt-1 flex items-center gap-1.5 text-xl font-black tabular-nums text-white">
            <PumpkinIcon size={18} />
            {you.total_pumpkins}
          </p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/30 p-3">
          <p className="text-[11px] text-text-secondary">{t('halloweenEvent.player.toTeam')}</p>
          <p className="mt-1 flex items-center gap-1.5 text-xl font-black tabular-nums text-white">
            <PumpkinIcon size={18} />
            {you.team_pumpkins ?? 0}
          </p>
        </div>
      </div>

      {!today.done && (
        <PumpkinButton icon={Gamepad2} onClick={() => go('/games')}>{t('halloweenEvent.player.play')}</PumpkinButton>
      )}
    </section>
  )
}

export default PlayerCard
